import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import placeholderImage from '../../assets/RGBNN.jpg';
import boxGreyTextLogo from '../../assets/Box_Grey_Text copy-01.svg';
import invertedLogo from '../../assets/Inverted_logo.png';
import qrCodeImage from '../../assets/adobe-express-qr-code.svg';
import windowsLogo from '../../assets/windows.png';
import linuxLogo from '../../assets/Linux-Logo.wine.svg';
// Import Lucide icons for features
import { Package, BarChart, Share2, Zap, Sliders, Search, MousePointer, MessageSquare, GitBranch, Laptop, BookOpen, GraduationCap } from 'lucide-react';

const ScreenshotBannerExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const bannerRef = useRef(null);
  
  // Set exact aspect ratio for banner
  const bannerAspectRatio = 845 / 2100;
  const bannerWidthMm = 845;
  const bannerHeightMm = 2100;
  
  // Handle preloading only for raster images to avoid CORS issues
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({
    placeholder: null,
    inverted: null,
    windows: null
  });

  // Preload only raster images, SVGs will be used directly
  useEffect(() => {
    const preloadImages = async () => {
      try {
        setProgress(10);
        
        // Preload each raster image as base64
        const preloadRasterImage = async (src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
            img.src = src;
          });
        };

        // Preload non-SVG images in parallel
        const [preloadedPlaceholder, preloadedInverted, preloadedWindows] = await Promise.all([
          preloadRasterImage(placeholderImage),
          preloadRasterImage(invertedLogo),
          preloadRasterImage(windowsLogo)
        ]);

        setPreloadedImages({
          placeholder: preloadedPlaceholder,
          inverted: preloadedInverted,
          windows: preloadedWindows
        });
        
        setImagesPreloaded(true);
        setProgress(0);
      } catch (error) {
        console.error('Failed to preload images:', error);
        alert('Failed to preload images. Some images may not display correctly.');
        setProgress(0);
      }
    };

    preloadImages();
  }, []);

  // Feature data for consistent styling and content
  const features = [
    {
      title: "3D Neural Network Visualization",
      description: "View neurons and connections in an interactive 3D space with customizable layout.",
      icon: <Package size={36} className="text-indigo-500" strokeWidth={1.5} />
    },
    {
      title: "Activation-Based Analysis",
      description: "Analyze neuron activations across different classes, with filtering and highlighting.",
      icon: <BarChart size={36} className="text-purple-500" strokeWidth={1.5} />
    },
    {
      title: "Connection Visualization",
      description: "Generate and explore connections between neurons to understand information flow.",
      icon: <Share2 size={36} className="text-pink-500" strokeWidth={1.5} />
    },
    {
      title: "Neural Network Optimization",
      description: "Optimize networks using active neuron analysis to improve model efficiency.",
      icon: <Zap size={36} className="text-indigo-500" strokeWidth={1.5} />
    },
    {
      title: "Neural Engineered Networks",
      description: "Engineer neural networks to improve performance and achieve desired behavior.",
      icon: <Sliders size={36} className="text-purple-500" strokeWidth={1.5} />
    },
    {
      title: "Neuron Selection & Inspection",
      description: "Select neurons to examine activation patterns and class responses.",
      icon: <Search size={36} className="text-pink-500" strokeWidth={1.5} />
    },
    {
      title: "Interactive UI",
      description: "Intuitive controls with immediate visual feedback on visualization changes.",
      icon: <MousePointer size={36} className="text-indigo-500" strokeWidth={1.5} />
    },
    {
      title: "LLM-Assisted Development",
      description: "Use Claude, OpenAI, or others to co-build, interpret, and explain models.",
      icon: <MessageSquare size={36} className="text-purple-500" strokeWidth={1.5} />
    },
    {
      title: "Node-Based Workflow Editor",
      description: "Build and connect model components visually like editing a graph.",
      icon: <GitBranch size={36} className="text-pink-500" strokeWidth={1.5} />
    },
  ];

  // Toggle preview mode - this makes the banner full size for accurate screenshot
  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
    // Scroll to top when entering preview mode
    if (!isPreviewMode) {
      window.scrollTo(0, 0);
    }
  };

  // Handle screenshot export to PNG
  const handleExportPNG = async () => {
    if (!bannerRef.current || isExporting || !imagesPreloaded) return;

    try {
      setIsExporting(true);
      setProgress(10);

      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setProgress(30);

      // Get a high-quality PNG of the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 4, // High DPI for good print quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        canvasWidth: bannerRef.current.offsetWidth,
        canvasHeight: bannerRef.current.offsetHeight,
        style: {
          // Ensure the banner is fully rendered before capture
          margin: 0,
          padding: 0,
        },
      });

      setProgress(80);

      // Trigger download
      const link = document.createElement('a');
      link.download = 'mechinterp-banner.png';
      link.href = dataUrl;
      link.click();

      setProgress(100);
      
      // Reset progress after a moment
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);

    } catch (error) {
      console.error('Failed to export PNG:', error);
      setIsExporting(false);
      setProgress(0);
      alert('Failed to export image. Please try again.');
    }
  };

  // Handle screenshot export to PDF
  const handleExportPDF = async () => {
    if (!bannerRef.current || isExporting || !imagesPreloaded) return;

    try {
      setIsExporting(true);
      setProgress(10);

      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setProgress(30);

      // Get a high-quality PNG of the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 3, // High DPI for good print quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        canvasWidth: bannerRef.current.offsetWidth,
        canvasHeight: bannerRef.current.offsetHeight,
        style: {
          // Ensure the banner is fully rendered before capture
          margin: 0,
          padding: 0,
        },
      });

      setProgress(70);

      // Create PDF with proper dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [bannerWidthMm, bannerHeightMm]
      });

      // Add image to cover entire PDF
      pdf.addImage(dataUrl, 'PNG', 0, 0, bannerWidthMm, bannerHeightMm);

      setProgress(90);
      
      // Save the PDF
      pdf.save('mechinterp-banner.pdf');

      setProgress(100);
      
      // Reset progress after a moment
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);

    } catch (error) {
      console.error('Failed to export PDF:', error);
      setIsExporting(false);
      setProgress(0);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Reset preview mode when component unmounts
  useEffect(() => {
    return () => {
      setIsPreviewMode(false);
    };
  }, []);

  // If images are not yet preloaded, show loading indicator
  if (!imagesPreloaded) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-gray-200">
        <div className="text-xl font-semibold mb-4">Preparing banner export tool...</div>
        <div className="w-64 bg-gray-300 h-2 rounded-full overflow-hidden">
          <div 
            className="bg-blue-600 h-full transition-all duration-300 ease-out" 
            style={{ width: `${progress || 10}%` }}
          ></div>
        </div>
        <p className="mt-4 text-gray-600">Preloading images to ensure high-quality export</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-200 min-h-screen flex flex-col items-center py-8">
      {/* Controls - only show when not in preview mode */}
      {!isPreviewMode && (
        <div className="w-full max-w-4xl mb-8 px-4">
          <div className="flex flex-wrap gap-4 mb-4">
            <button 
              onClick={togglePreviewMode}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Preview Full Size
            </button>
            <button 
              onClick={handleExportPNG}
              disabled={isExporting}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Export as PNG
            </button>
            <button 
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Export as PDF
            </button>
            <Link 
              to="/a5-banner" 
              className="px-6 py-3 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 transition-colors"
            >
              Switch to A5 Banner
            </Link>
          </div>
          <p className="mt-2 text-gray-600">
            Banner size: {bannerWidthMm} x {bannerHeightMm}mm - Aspect ratio: {bannerAspectRatio.toFixed(3)}
          </p>
          {isExporting && (
            <div className="mt-2">
              <div className="w-full bg-gray-300 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-blue-600 h-full transition-all duration-300 ease-out" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">Exporting: {progress}% complete</p>
            </div>
          )}
        </div>
      )}

      {/* Exit preview button - only show when in preview mode */}
      {isPreviewMode && !isExporting && (
        <div className="fixed top-4 right-4 z-50">
          <button 
            onClick={togglePreviewMode}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors shadow-lg"
          >
            Exit Preview
          </button>
        </div>
      )}
      
      {/* The banner itself */}
      <div 
        ref={bannerRef}
        className={`text-white ${!isPreviewMode ? 'border-2 border-blue-500' : ''}`}
        style={{ 
          width: isPreviewMode ? `${bannerWidthMm}px` : '100%',
          maxWidth: isPreviewMode ? `${bannerWidthMm}px` : '845px',
          aspectRatio: `${bannerAspectRatio}`,
          margin: '0 auto',
          background: 'linear-gradient(135deg, #111827 0%, #000000 100%)',
        }}
      >
        {/* Header with logos - Now with solid black background */}
        <div className="flex justify-between items-center px-6 py-3 bg-black" style={{ height: '6%' }}>
          {/* Left logo - Box_Grey_Text - Use SVG directly */}
          <div className="flex items-center">
            <img 
              src={boxGreyTextLogo} 
              alt="Mechinterp Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          {/* Right logo - Inverted_logo */}
          <img 
            src={preloadedImages.inverted || invertedLogo} 
            alt="Inverted Logo" 
            className="h-16 w-auto"
          />
        </div>
        
        {/* Hero section with network visualization image */}
        <div className="relative flex justify-center items-center" style={{ height: '26%' }}>
          {/* Placeholder image from assets */}
          <div className="w-full h-full bg-gray-900 relative overflow-hidden">
            <img 
              src={preloadedImages.placeholder || placeholderImage} 
              alt="Neural Network Visualization" 
              className="w-full h-full object-cover"
              style={{ opacity: 0.7 }}
            />
          </div>
          
          {/* Text overlay - Adjusted to be more distinctly right of center */}
          <div className="absolute inset-0 flex items-center" style={{ paddingLeft: '10%' }}>
            <div className="bg-black bg-opacity-60 backdrop-blur-sm p-5 sm:p-8 rounded-lg max-w-xl mx-4">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">Opening the Black Box</h1>
              <p className="text-gray-300 text-lg md:text-xl text-center">
                The first commercial platform for mechanistic interpretability — visualise, optimise and truly understand AI behavior.
              </p>
            </div>
          </div>
        </div>
        
        {/* Mailing list section */}
        <div className="py-4 border-t border-gray-800">
          <div className="flex items-center justify-between px-8">
            {/* Email icon and text */}
            <div className="flex items-center space-x-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" 
                  fill="rgba(255, 255, 255, 0.8)" />
              </svg>
              <span className="text-white text-lg">Join the mailing list for updates at <a href="https://mechinterp.ai" className="text-blue-400">mechinterp.ai</a></span>
            </div>

            {/* QR code - Use SVG directly */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-white flex items-center justify-center rounded-lg">
                <img 
                  src={qrCodeImage} 
                  alt="QR Code for website"
                  className="w-16 h-16 object-contain" 
                />
              </div>
              <p className="text-xs text-center text-white font-bold mt-1">mechinterp.ai</p>
            </div>
          </div>
        </div>
        
        {/* Who uses Mechinterp section */}
        <div className="py-4 bg-transparent">
          <h2 className="text-2xl font-bold text-center mb-4">Who uses Mechinterp</h2>
          <div className="grid grid-cols-3 gap-3 px-4">
            {/* Engineers - Added border-white/30 for a thin white border */}
            <div className="bg-gray-900 p-5 rounded-lg border border-white/20" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(17, 24, 39), rgba(30, 58, 138, 0.3))' }}>
              <div className="flex justify-center mb-3">
                <Laptop size={36} className="text-blue-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-white">Engineers</h3>
              <p className="text-gray-300 text-base text-center">
                Debug, refine, and optimize models enhancing performance and reliability.
              </p>
            </div>
            
            {/* Researchers - Added border-white/30 for a thin white border */}
            <div className="bg-gray-900 p-5 rounded-lg border border-white/20" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(17, 24, 39), rgba(88, 28, 135, 0.3))' }}>
              <div className="flex justify-center mb-3">
                <BookOpen size={36} className="text-purple-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-white">Researchers</h3>
              <p className="text-gray-300 text-base text-center">
                Build more interpretable, trustworthy, and effective AI systems.
              </p>
            </div>
            
            {/* Academic Institutions - Added border-white/30 for a thin white border */}
            <div className="bg-gray-900 p-5 rounded-lg border border-white/20" style={{ backgroundImage: 'linear-gradient(to bottom right, rgb(17, 24, 39), rgba(131, 24, 67, 0.3))' }}>
              <div className="flex justify-center mb-3">
                <GraduationCap size={36} className="text-pink-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-white">Academic Institutions</h3>
              <p className="text-gray-300 text-base text-center">
                Help students understand how modern AI models function.
              </p>
            </div>
          </div>
        </div>
        
        {/* Key Features section */}
        <div className="py-4 bg-transparent">
          <h2 className="text-2xl font-bold text-center mb-4">Key Features</h2>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-3 gap-3">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gray-900 p-5 rounded-lg flex flex-col items-center border border-white/15" 
                  style={{ backgroundColor: 'rgb(17, 24, 39)' }}
                >
                  <div className="flex justify-center mb-3">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center text-white">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-base text-center">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer Section with OS Logos - Only logos shifted right */}
        <div className="border-t border-gray-800 py-4 pt-3">
          <div className="text-center">
            {/* Only the logos container is shifted right */}
            <div className="flex justify-center items-center gap-6 mb-3 ml-8">
              <img 
                src={preloadedImages.windows || windowsLogo} 
                alt="Windows Logo" 
                className="h-12 w-auto" 
              />
              <img 
                src={linuxLogo} 
                alt="Linux Logo" 
                className="h-20 w-auto" 
              />
            </div>
            <p className="text-sm text-gray-300">Available for Windows and Linux</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenshotBannerExport;